/**
 * The impact picture: the critical path before and after an event, as bars, so a
 * reviewer sees the delay instead of only reading the number.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Loader2, GanttChartSquare } from 'lucide-react';

const day = 86400000;

export default function ImpactGantt({ claimId, method }) {
  const [data, setData] = useState(null);
  const [eventId, setEventId] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (ev = '') => {
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/eot/claims/${claimId}/analysis/${method}/impact${ev ? `?eventId=${ev}` : ''}`);
      setData(d);
      setEventId(d.eventId || '');
    } catch { setData(null); } finally { setLoading(false); }
  }, [claimId, method]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="py-6 text-center text-xs text-muted-foreground" data-testid="eot-impact-loading">
      <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />Scheduling the impacted run…</div>;
  }
  if (!data || !(data.bars || []).length) return null;

  const dates = data.bars.flatMap(b => [b.beforeStart, b.beforeFinish, b.afterStart, b.afterFinish])
    .filter(Boolean).map(d => new Date(d).getTime());
  const min = Math.min(...dates, new Date(data.completionBefore).getTime());
  const max = Math.max(...dates, new Date(data.completionAfter).getTime());
  const span = Math.max(1, (max - min) / day);
  const pct = (d) => `${((new Date(d).getTime() - min) / day / span) * 100}%`;
  const width = (a, b) => `${Math.max(0.6, (((new Date(b).getTime() - new Date(a).getTime()) / day) + 1) / span * 100)}%`;

  return (
    <div className="rounded-lg border border-border p-3 space-y-3" data-testid="eot-impact-chart">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-foreground font-medium flex items-center gap-1.5">
          <GanttChartSquare className="w-3.5 h-3.5 text-[#6B5CE7]" />
          The critical path before and after {data.eventId}
          <span className="text-muted-foreground font-normal">
            · {data.impactDays} day(s) on {data.programme}{data.dataDate ? ` (${data.dataDate})` : ''}
          </span>
        </p>
        <select value={eventId} onChange={e => load(e.target.value)} data-testid="eot-impact-event"
                className="h-8 rounded-md border border-border bg-background text-foreground text-[11px] px-2">
          {(data.events || []).map(e => <option key={e.eventId} value={e.eventId}>{e.eventId} — {e.title}</option>)}
        </select>
      </div>

      <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><i className="inline-block w-3 h-2 rounded-sm bg-slate-400" />as planned in this update</span>
        <span className="flex items-center gap-1"><i className="inline-block w-3 h-2 rounded-sm bg-[#6B5CE7]" />after the event is impacted in</span>
        <span className="flex items-center gap-1"><i className="inline-block w-3 h-2 rounded-sm bg-amber-500" />the event itself</span>
        <span className="ml-auto">{data.completionBefore} → <strong className="text-foreground">{data.completionAfter}</strong></span>
      </div>

      <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
        {data.bars.map(b => (
          <div key={b.id} className="grid grid-cols-[200px_1fr_52px] items-center gap-2"
               data-testid={`eot-impact-bar-${b.id}`}>
            <p className={`text-[10px] truncate ${b.isFragnet ? 'text-amber-500 font-medium' : 'text-muted-foreground'}`}
               title={b.name}>{b.name}</p>
            <div className="relative h-5 rounded bg-muted/40">
              {b.beforeStart && (
                <div className="absolute top-0.5 h-1.5 rounded-sm bg-slate-400/70"
                     style={{ left: pct(b.beforeStart), width: width(b.beforeStart, b.beforeFinish) }} />
              )}
              <div className={`absolute bottom-0.5 h-2 rounded-sm ${b.isFragnet ? 'bg-amber-500' : 'bg-[#6B5CE7]'}`}
                   style={{ left: pct(b.afterStart), width: width(b.afterStart, b.afterFinish) }} />
            </div>
            <p className="text-[10px] text-right text-muted-foreground">
              {b.shiftDays > 0 ? `+${b.shiftDays}d` : b.shiftDays < 0 ? `${b.shiftDays}d` : b.shiftDays === 0 ? '—' : ''}
            </p>
          </div>
        ))}
      </div>
      {data.caption && <p className="text-[10px] text-muted-foreground" data-testid="eot-impact-caption">{data.caption}</p>}
      {data.assumption && <p className="text-[10px] text-amber-600">{data.assumption}</p>}
    </div>
  );
}
